/**
 * CATÁLOGOS NORMATIVOS DOS CÁLCULOS DE INTEGRIDADE
 *
 * Materiais, tensões admissíveis por temperatura, fatores E / W / Y,
 * densidades de cupom e edições normativas vivem aqui — nunca dentro de uma
 * página. A página apenas consulta.
 *
 * Nenhum lookup deste arquivo devolve um valor "aproximado" quando não há
 * correspondência: ou existe linha de tabela aplicável, ou o retorno é null e
 * o cálculo fica bloqueado até o engenheiro resolver.
 */

/** Estado de conferência de uma linha de catálogo pela engenharia do cliente. */
export type CatalogVerification = "VALIDATED" | "PENDING_VALIDATION";

export type ProductForm =
  | "SEAMLESS"
  | "ERW"
  | "EFW"
  | "FURNACE_BUTT_WELDED";

export const PRODUCT_FORM_LABELS: Record<ProductForm, string> = {
  SEAMLESS: "Sem costura (seamless)",
  ERW: "Soldado por resistência elétrica (ERW)",
  EFW: "Soldado por fusão elétrica (EFW)",
  FURNACE_BUTT_WELDED: "Soldado de topo em forno (furnace butt welded)"
};

/** Classe de material para o coeficiente Y da Tabela 304.1.1. */
export type MaterialClass = "FERRITIC" | "AUSTENITIC" | "OTHER_DUCTILE" | "CAST_IRON";

export const MATERIAL_CLASS_LABELS: Record<MaterialClass, string> = {
  FERRITIC: "Aços ferríticos",
  AUSTENITIC: "Aços austeníticos",
  OTHER_DUCTILE: "Outros metais dúcteis",
  CAST_IRON: "Ferro fundido"
};

export interface PipingMaterial {
  id: string;
  /** Especificação, ex.: "ASTM A106". */
  specification: string;
  /** Grau, ex.: "Gr. B". */
  grade: string;
  /** Forma do produto — define o fator de qualidade E. */
  productForm: ProductForm;
  materialClass: MaterialClass;
  family: string;
  /** g/cm³ — usada pelo cálculo NACE quando o cupom é deste material. */
  densityGramsPerCm3: number | null;
  /** Faixa de temperatura de aplicação do material, em °C. */
  minTemperatureCelsius: number | null;
  maxTemperatureCelsius: number | null;
}

export function formatMaterialLabel(material: PipingMaterial): string {
  return material.grade ? `${material.specification} ${material.grade}` : material.specification;
}

export interface AllowableStressPoint {
  temperatureCelsius: number;
  /** Tensão admissível básica, em MPa. */
  stressMPa: number;
}

/** Tabela de tensão admissível de UM material em UMA edição normativa. */
export interface AllowableStressTable {
  materialId: string;
  /** Edição normativa a que a tabela pertence (ver NormEdition.id). */
  editionId: string;
  points: AllowableStressPoint[];
  source: string;
  verification: CatalogVerification;
}

export interface QualityFactorRow {
  productForm: ProductForm;
  value: number;
  source: string;
  verification: CatalogVerification;
}

export interface WeldJointFactorRow {
  materialClass: MaterialClass;
  /** W vale este valor até esta temperatura, inclusive. */
  maxTemperatureCelsius: number;
  value: number;
  source: string;
  verification: CatalogVerification;
}

export interface YCoefficientRow {
  materialClass: MaterialClass;
  temperatureCelsius: number;
  value: number;
  source: string;
  verification: CatalogVerification;
}

export interface NormEdition {
  id: string;
  norm: string;
  edition: string;
  isDefault: boolean;
}

export interface CouponMaterial {
  id: string;
  name: string;
  densityGramsPerCm3: number;
  source: string;
  verification: CatalogVerification;
}

/** Resultado de qualquer consulta normativa: o valor nunca vem sozinho, sempre com a origem. */
export interface CatalogLookup<T> {
  value: T;
  source: string;
  editionId?: string;
  verification: CatalogVerification;
  /** true quando o valor foi interpolado linearmente entre duas linhas da tabela. */
  interpolated?: boolean;
}

export interface IntegrityCatalogData {
  materials: PipingMaterial[];
  allowableStressTables: AllowableStressTable[];
  qualityFactors: QualityFactorRow[];
  weldJointFactors: WeldJointFactorRow[];
  yCoefficients: YCoefficientRow[];
  couponMaterials: CouponMaterial[];
  editions: NormEdition[];
}

/**
 * Tensão admissível S do material na temperatura de projeto.
 *
 * Interpola linearmente entre as temperaturas tabeladas (permitido pela
 * B31.3). Fora da faixa da tabela devolve null — extrapolar tensão admissível
 * seria inventar valor normativo.
 */
export function lookupAllowableStress(
  tables: readonly AllowableStressTable[],
  materialId: string,
  temperatureCelsius: number | null,
  editionId: string
): CatalogLookup<number> | null {
  if (temperatureCelsius === null || !Number.isFinite(temperatureCelsius)) return null;

  const table = tables.find(t => t.materialId === materialId && t.editionId === editionId);
  if (!table || table.points.length === 0) return null;

  const points = [...table.points].sort((a, b) => a.temperatureCelsius - b.temperatureCelsius);
  const first = points[0]!;
  const last = points[points.length - 1]!;

  if (temperatureCelsius < first.temperatureCelsius || temperatureCelsius > last.temperatureCelsius) {
    return null;
  }

  const exact = points.find(p => p.temperatureCelsius === temperatureCelsius);
  if (exact) {
    return { value: exact.stressMPa, source: table.source, editionId: table.editionId, verification: table.verification };
  }

  for (let i = 0; i < points.length - 1; i++) {
    const lower = points[i]!;
    const upper = points[i + 1]!;
    if (temperatureCelsius > lower.temperatureCelsius && temperatureCelsius < upper.temperatureCelsius) {
      const span = upper.temperatureCelsius - lower.temperatureCelsius;
      const ratio = (temperatureCelsius - lower.temperatureCelsius) / span;
      const value = lower.stressMPa + ratio * (upper.stressMPa - lower.stressMPa);
      return {
        value,
        source: table.source,
        editionId: table.editionId,
        verification: table.verification,
        interpolated: true
      };
    }
  }

  return null;
}

/** Fator de qualidade E, determinado pela forma do produto (com ou sem costura). */
export function lookupQualityFactor(
  rows: readonly QualityFactorRow[],
  productForm: ProductForm | null
): CatalogLookup<number> | null {
  if (!productForm) return null;
  const row = rows.find(r => r.productForm === productForm);
  return row ? { value: row.value, source: row.source, verification: row.verification } : null;
}

/**
 * Fator W da junta soldada. Cada linha vale até a sua temperatura máxima;
 * acima da maior faixa cadastrada o retorno é null (entrada manual com
 * justificativa), em vez de assumir 1,00 numa temperatura onde a norma reduz W.
 */
export function lookupWeldJointFactor(
  rows: readonly WeldJointFactorRow[],
  materialClass: MaterialClass | null,
  temperatureCelsius: number | null
): CatalogLookup<number> | null {
  if (!materialClass || temperatureCelsius === null || !Number.isFinite(temperatureCelsius)) return null;

  const candidates = rows
    .filter(r => r.materialClass === materialClass && temperatureCelsius <= r.maxTemperatureCelsius)
    .sort((a, b) => a.maxTemperatureCelsius - b.maxTemperatureCelsius);

  const row = candidates[0];
  return row ? { value: row.value, source: row.source, verification: row.verification } : null;
}

/**
 * Coeficiente Y da Tabela 304.1.1. A tabela é degrau por temperatura: usa-se a
 * linha da maior temperatura tabelada que não ultrapasse a de projeto.
 */
export function lookupYCoefficient(
  rows: readonly YCoefficientRow[],
  materialClass: MaterialClass | null,
  temperatureCelsius: number | null
): CatalogLookup<number> | null {
  if (!materialClass || temperatureCelsius === null || !Number.isFinite(temperatureCelsius)) return null;

  const candidates = rows
    .filter(r => r.materialClass === materialClass && r.temperatureCelsius <= temperatureCelsius)
    .sort((a, b) => b.temperatureCelsius - a.temperatureCelsius);

  const row = candidates[0];
  if (row) return { value: row.value, source: row.source, verification: row.verification };

  // Abaixo da menor temperatura tabelada vale a primeira linha da tabela.
  const lowest = rows
    .filter(r => r.materialClass === materialClass)
    .sort((a, b) => a.temperatureCelsius - b.temperatureCelsius)[0];

  return lowest ? { value: lowest.value, source: lowest.source, verification: lowest.verification } : null;
}

/** Densidade do material do cupom de corrosão (g/cm³). */
export function lookupCouponDensity(
  materials: readonly CouponMaterial[],
  couponMaterialId: string | null
): CatalogLookup<number> | null {
  if (!couponMaterialId) return null;
  const material = materials.find(m => m.id === couponMaterialId);
  return material
    ? { value: material.densityGramsPerCm3, source: material.source, verification: material.verification }
    : null;
}

/**
 * Casa o material escrito no cadastro da linha ("ASTM A106 Gr. B", "A106-B")
 * com uma entrada do catálogo. Só aceita correspondência inequívoca — na
 * dúvida devolve null e o usuário escolhe o material na lista.
 */
export function matchMaterialFromRegistry(
  materials: readonly PipingMaterial[],
  registryText: string | null | undefined,
  specification?: string | null,
  grade?: string | null
): PipingMaterial | null {
  const normalize = (value: string | null | undefined) =>
    String(value ?? "")
      .toUpperCase()
      .replace(/GRADE/g, "GR")
      .replace(/[^A-Z0-9]/g, "");

  if (specification) {
    const wantedSpec = normalize(specification);
    const wantedGrade = normalize(grade);
    const bySpec = materials.filter(m => normalize(m.specification) === wantedSpec);
    if (bySpec.length === 1 && !wantedGrade) return bySpec[0]!;
    const exact = bySpec.find(m => normalize(m.grade) === wantedGrade);
    if (exact) return exact;
  }

  const wanted = normalize(registryText);
  if (!wanted) return null;

  const direct = materials.find(m => normalize(formatMaterialLabel(m)) === wanted);
  if (direct) return direct;

  const contained = materials.filter(m => wanted.includes(normalize(formatMaterialLabel(m))));
  return contained.length === 1 ? contained[0]! : null;
}
